export type PlacementElementType = 'signature' | 'rubric' | 'customField';

export interface PlacementValidationElement {
  key: string;
  fileName: string;
  page: string;
  type: PlacementElementType;
  rect: DOMRect;
  height: number;
}

const HEIGHT_BASE_BOARD = 40;
const HEIGHT_TOP_BOARD_DEFAULT = 18;
const SPATIAL_GRID_CELL_SIZE = 180;

export function calculatePlacementValidity(
  elements: PlacementValidationElement[],
  currentFileName: string,
  dropzonesByPage: Map<string, DOMRect>,
  targetPages?: Set<string>
): Map<string, boolean> {
  const validityByElementKey = new Map<string, boolean>();
  const elementsByPage = groupElementsByPage(elements, currentFileName, dropzonesByPage, targetPages);

  elementsByPage.forEach((pageElements, page) => {
    const dropzoneRect = dropzonesByPage.get(page);
    if (!dropzoneRect || pageElements.length === 0) {
      return;
    }

    const overlapsByIndex = calculateOverlapsByIndex(pageElements);
    for (let index = 0; index < pageElements.length; index++) {
      const currentElement = pageElements[index];
      const isValid = isElementInsidePageBounds(
        overlapsByIndex[index],
        currentElement.type,
        currentElement.rect,
        dropzoneRect,
        currentElement.height
      );

      validityByElementKey.set(currentElement.key, isValid);
    }
  });

  return validityByElementKey;
}

function groupElementsByPage(
  elements: PlacementValidationElement[],
  currentFileName: string,
  dropzonesByPage: Map<string, DOMRect>,
  targetPages?: Set<string>
): Map<string, PlacementValidationElement[]> {
  const elementsByPage = new Map<string, PlacementValidationElement[]>();
  const hasPageFilter = !!targetPages && targetPages.size > 0;

  for (let index = 0; index < elements.length; index++) {
    const element = elements[index];
    if (!element.page || element.fileName !== currentFileName || !dropzonesByPage.has(element.page)) {
      continue;
    }

    if (hasPageFilter && !targetPages.has(element.page)) {
      continue;
    }

    const pageElements = elementsByPage.get(element.page) || [];
    pageElements.push(element);
    elementsByPage.set(element.page, pageElements);
  }

  return elementsByPage;
}

function calculateOverlapsByIndex(elements: PlacementValidationElement[]): number[] {
  const overlapsByIndex = new Array<number>(elements.length).fill(0);
  if (elements.length < 2) {
    return overlapsByIndex;
  }

  const grid = new Map<string, number[]>();

  for (let index = 0; index < elements.length; index++) {
    const cellKeys = getRectCellKeys(elements[index].rect);
    for (let keyIndex = 0; keyIndex < cellKeys.length; keyIndex++) {
      const cellKey = cellKeys[keyIndex];
      const elementIndexes = grid.get(cellKey) || [];
      elementIndexes.push(index);
      grid.set(cellKey, elementIndexes);
    }
  }

  const processedPairs = new Set<string>();
  for (let index = 0; index < elements.length; index++) {
    const currentCellKeys = getRectCellKeys(elements[index].rect);
    const candidateIndexes = new Set<number>();

    for (let keyIndex = 0; keyIndex < currentCellKeys.length; keyIndex++) {
      const gridIndexes = grid.get(currentCellKeys[keyIndex]) || [];
      for (let gridIndex = 0; gridIndex < gridIndexes.length; gridIndex++) {
        const candidateIndex = gridIndexes[gridIndex];
        if (candidateIndex !== index) {
          candidateIndexes.add(candidateIndex);
        }
      }
    }

    candidateIndexes.forEach((candidateIndex) => {
      const minIndex = Math.min(index, candidateIndex);
      const maxIndex = Math.max(index, candidateIndex);
      const pairKey = `${minIndex}:${maxIndex}`;
      if (processedPairs.has(pairKey)) {
        return;
      }
      processedPairs.add(pairKey);

      if (overlaps(elements[index].rect, elements[candidateIndex].rect)) {
        overlapsByIndex[index] += 1;
        overlapsByIndex[candidateIndex] += 1;
      }
    });
  }

  return overlapsByIndex;
}

function getRectCellKeys(rect: DOMRect): string[] {
  const left = Math.floor(rect.left / SPATIAL_GRID_CELL_SIZE);
  const right = Math.floor(rect.right / SPATIAL_GRID_CELL_SIZE);
  const top = Math.floor(rect.top / SPATIAL_GRID_CELL_SIZE);
  const bottom = Math.floor(rect.bottom / SPATIAL_GRID_CELL_SIZE);

  const keys: string[] = [];
  for (let x = left; x <= right; x++) {
    for (let y = top; y <= bottom; y++) {
      keys.push(`${x}:${y}`);
    }
  }

  return keys;
}

function overlaps(currentRect: DOMRect, nextRect: DOMRect): boolean {
  if (!(currentRect.x > 0 || nextRect.x > 0)) {
    return false;
  }

  const validateY = currentRect.bottom < nextRect.top || currentRect.top > nextRect.bottom;
  const validateX = currentRect.right < nextRect.left || currentRect.left > nextRect.right;

  return !validateY && !validateX;
}

function isElementInsidePageBounds(
  invalidOverlaps: number,
  type: PlacementElementType,
  elementRect: DOMRect,
  dropzoneRect: DOMRect,
  elementHeight: number
): boolean {
  if (elementRect.x === 0 && elementRect.y === 0) {
    return true;
  }

  const heightTopBoard = type === 'rubric' ? 18 : HEIGHT_TOP_BOARD_DEFAULT;
  const widthBoard = elementRect.width + 20;

  const signaturePositionY = dropzoneRect.height - (elementRect.top - dropzoneRect.top) - elementHeight;
  const signaturePositionX = elementRect.left - (dropzoneRect.left + dropzoneRect.width);
  const signaturePositionTop = elementRect.top - dropzoneRect.top;
  const signaturePositionXRight = elementRect.right - (dropzoneRect.right - dropzoneRect.width);

  return !(
    invalidOverlaps > 0 ||
    signaturePositionY < HEIGHT_BASE_BOARD ||
    signaturePositionTop < heightTopBoard ||
    signaturePositionX > 0 ||
    Math.abs(signaturePositionX) < widthBoard ||
    Math.abs(signaturePositionXRight) < widthBoard
  );
}
